import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { ApiError } from '@/lib/api';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { deleteBlogDraft, getBlogDraft, listBlogDrafts, type BlogDraftSummary } from '@/data/sources-content';
import { BlogDraftView } from '@/voice/actions/BlogDraftView';
import { SeoAutopilotPanel } from '@/pages/social/SeoAutopilotPanel';

/* ----------------------------------------------------------------------------
 * Blog & Articles library (AI Content Studio).
 *
 * Lists SAVED long-form drafts from bc_content_drafts so a blog generated by
 * Aura is discoverable + REOPENABLE after its generation card closed. It is a
 * SEPARATE category from "Recent content" (bc_social_content_items): blog and
 * social records are never merged.
 *
 * DOMAIN ISOLATION (P0 Content Studio, 2026-07-21): bc_content_drafts carries a
 * populated project_id, so the library is scoped to the SELECTED domain (the
 * shell's "Viewing" project via useActiveDomain). Switching Viewing changes the
 * queryKey, so React Query REFETCHES and stale previous-domain drafts are never
 * shown. Drafts with NO domain provenance (project_id IS NULL) are reachable
 * ONLY through the explicit "Unassigned" toggle — never attributed to a domain.
 *
 * Read-only: a ready draft reopens into the existing BlogDraftView (title/slug/
 * meta/body/CTA/SEO evidence + copy-text/copy-HTML). Nothing here generates,
 * mutates, publishes, schedules or sends.
 * -------------------------------------------------------------------------- */

function statusBadge(status: string): { tone: 'success' | 'danger' | 'warning' | 'neutral'; label: string } {
  switch (status) {
    case 'ready':
      return { tone: 'success', label: 'Ready' };
    case 'failed':
      return { tone: 'danger', label: 'Failed' };
    case 'generating':
      return { tone: 'warning', label: 'Generating' };
    default:
      return { tone: 'neutral', label: status ? status : 'Draft' };
  }
}

function deleteErrorMessage(err: unknown): string {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
    return 'Your session has expired or you are not permitted to delete this draft. Refresh and try again.';
  }
  return 'Could not delete this draft. Try again.';
}

export function BlogLibrarySection() {
  const [openId, setOpenId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<BlogDraftSummary | null>(null);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const queryClient = useQueryClient();

  // The shell's single source of truth for the "Viewing" domain. null => the
  // "All domains" lens (no single domain selected).
  const { active } = useActiveDomain();
  const projectId = active?.projectId ?? null;
  const domainLabel = active ? active.displayName || active.hostname || 'this domain' : null;

  // Domain-scoped query key: switching Viewing (or toggling Unassigned) yields a
  // NEW key, so React Query refetches and previous-domain rows never linger.
  const draftsKey = useMemo(
    () => ['content-blog-drafts', showUnassigned ? 'unassigned' : (projectId ?? 'none')] as const,
    [showUnassigned, projectId],
  );

  const drafts = useQuery({
    queryKey: draftsKey,
    queryFn: () => listBlogDrafts(showUnassigned ? { unassigned: true } : { projectId }),
  });

  // Optimistic + honest: drop the row locally, roll back on failure, then
  // invalidate the ACTIVE scope's key so the list reconciles with the server's
  // soft-deleted truth. The soft delete is reversible server-side; the row is
  // retained + restorable.
  const del = useMutation({
    mutationFn: (id: number) => deleteBlogDraft(id),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: draftsKey });
      const prev = queryClient.getQueryData<BlogDraftSummary[]>(draftsKey);
      queryClient.setQueryData<BlogDraftSummary[]>(draftsKey, (old) =>
        old ? old.filter((d) => d.id !== id) : old,
      );
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(draftsKey, ctx.prev);
    },
    onSuccess: () => setPendingDelete(null),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: draftsKey }),
  });

  const scopeNote = showUnassigned
    ? 'Across workspace · unassigned drafts (no domain)'
    : domainLabel
      ? `Viewing: ${domainLabel}`
      : 'No domain selected';

  return (
    <section className="space-y-3">
      <SeoAutopilotPanel />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold text-text-primary">Blog &amp; articles</h2>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-text-secondary">
          Long-form drafts Aura generated for you, scoped to the domain you are viewing. Open one to
          review, copy the text or HTML, or repurpose it for social. Nothing here is published.
        </p>
        <button
          type="button"
          onClick={() => setShowUnassigned((v) => !v)}
          className="shrink-0 rounded-full border border-border-subtle px-3 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-ai hover:text-ai focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/40"
          aria-pressed={showUnassigned}
        >
          {showUnassigned
            ? domainLabel
              ? `Show ${domainLabel} drafts`
              : 'Show domain drafts'
            : 'Show unassigned'}
        </button>
      </div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">{scopeNote}</p>

      {drafts.isLoading ? (
        <LoadingState rows={2} />
      ) : drafts.isError ? (
        <ErrorState error={drafts.error} onRetry={() => void drafts.refetch()} />
      ) : (drafts.data?.length ?? 0) === 0 ? (
        showUnassigned ? (
          <EmptyState
            title="No unassigned drafts"
            teach="Drafts with no domain provenance show here. As Aura generates blogs under a selected domain, they appear in that domain's view instead."
          />
        ) : projectId == null ? (
          <EmptyState
            title="Choose a domain to see its blog drafts"
            teach="Select a domain to view (top of the app) to see its saved blogs here — or use “Show unassigned” for drafts that have no domain."
          />
        ) : (
          <EmptyState
            title="No saved blogs yet for this domain"
            teach="Ask Aura to write a blog or article for this domain. Saved drafts appear here so you can reopen them any time — they no longer disappear when the card closes."
          />
        )
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {drafts.data!.map((d) => (
            <BlogCard key={d.id} draft={d} onOpen={() => setOpenId(d.id)} onDelete={() => setPendingDelete(d)} />
          ))}
        </div>
      )}

      {openId !== null && <ReopenDialog id={openId} onClose={() => setOpenId(null)} />}
      {pendingDelete && (
        <ConfirmDeleteDialog
          draft={pendingDelete}
          onCancel={() => { if (!del.isPending) setPendingDelete(null); }}
          onConfirm={() => del.mutate(pendingDelete.id)}
          pending={del.isPending}
          error={del.isError ? deleteErrorMessage(del.error) : null}
        />
      )}
    </section>
  );
}

function BlogCard({ draft, onOpen, onDelete }: { draft: BlogDraftSummary; onOpen: () => void; onDelete: () => void }) {
  const st = statusBadge(draft.status);
  const reopenable = draft.status === 'ready';
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface p-4 transition-colors hover:border-ai">
      <div className="flex items-start justify-between gap-2">
        {reopenable ? (
          <button
            onClick={onOpen}
            className="min-w-0 flex-1 truncate text-left text-sm font-medium text-text-primary hover:text-ai"
          >
            {draft.title || 'Untitled draft'}
          </button>
        ) : (
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
            {draft.title || 'Untitled draft'}
          </span>
        )}
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      <div className="flex min-w-0 items-center gap-2 text-[11px] text-text-tertiary">
        <span className="truncate">/{draft.slug || 'no-slug'}</span>
        {draft.generatedAt ? (
          <>
            <span>·</span>
            <span className="shrink-0">{draft.generatedAt.slice(0, 10)}</span>
          </>
        ) : null}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        {reopenable ? (
          <button
            onClick={onOpen}
            className="self-start rounded-full border border-ai/40 bg-ai/5 px-3 py-1 text-xs font-semibold text-ai transition-colors hover:bg-ai/10"
          >
            Reopen
          </button>
        ) : (
          <p className="text-[11px] text-text-tertiary">
            {draft.status === 'failed'
              ? 'This draft did not generate — regenerate it from Aura.'
              : 'This draft is still generating.'}
          </p>
        )}
        <button
          onClick={onDelete}
          className="shrink-0 rounded px-1 text-[11px] font-medium text-text-tertiary transition-colors hover:text-danger focus:text-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40"
          aria-label={`Delete draft${draft.title ? `: ${draft.title}` : ''}`}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function ReopenDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  // The active "Viewing" domain — tags the repurposed social drafts to this
  // domain (the library is domain-scoped, so it is the source blog's domain).
  const { active } = useActiveDomain();
  const projectId = active?.projectId ?? null;
  const draft = useQuery({
    queryKey: ['content-blog-draft', id],
    queryFn: () => getBlogDraft(id),
    staleTime: 30_000,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px]" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Saved blog draft"
        className="relative z-10 mt-10 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border-default bg-surface p-4 shadow-lg sm:mt-0"
      >
        {draft.isLoading ? (
          <LoadingState rows={4} />
        ) : draft.isError ? (
          <div className="space-y-3">
            <ErrorState error={draft.error} onRetry={() => void draft.refetch()} />
            <button
              onClick={onClose}
              className="rounded-sm px-2 py-1.5 text-[11px] text-text-tertiary hover:bg-hover hover:text-text-primary"
            >
              Close
            </button>
          </div>
        ) : draft.data ? (
          <BlogDraftView
            draft={draft.data}
            onDismiss={onClose}
            projectId={projectId}
            onRepurpose={() => {
              // New social DRAFTS were created from this blog draft — refresh the
              // Content Studio content list so they appear for review.
              void queryClient.invalidateQueries({ queryKey: ['social-content'] });
            }}
            onSaved={(d) => {
              // Reflect the saved edit immediately in this dialog, and refresh the
              // library cards (title/slug) from the server's saved truth.
              queryClient.setQueryData(['content-blog-draft', id], d);
              void queryClient.invalidateQueries({ queryKey: ['content-blog-drafts'] });
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function ConfirmDeleteDialog({
  draft, onCancel, onConfirm, pending, error,
}: {
  draft: BlogDraftSummary;
  onCancel: () => void;
  onConfirm: () => void;
  pending: boolean;
  error: string | null;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px]" onClick={() => !pending && onCancel()} aria-hidden />
      <div role="alertdialog" aria-modal="true" aria-label="Delete draft" className="relative z-10 mt-16 w-full max-w-sm rounded-xl border border-border-default bg-surface shadow-lg sm:mt-0">
        <header className="border-b border-border-subtle px-5 py-3">
          <h2 className="text-sm font-semibold text-text-primary">Delete this draft?</h2>
        </header>
        <div className="space-y-3 px-5 py-4">
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">{draft.title || 'Untitled draft'}</span> will be removed from your library. This can be restored — it is a soft delete, and the action is recorded for audit.
          </p>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onCancel} disabled={pending}>Cancel</Button>
            <button
              onClick={onConfirm}
              disabled={pending}
              className="inline-flex h-control items-center justify-center rounded-md bg-danger px-4 text-sm font-medium text-white transition-colors hover:bg-danger/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40 disabled:opacity-60"
            >
              {pending ? 'Deleting…' : 'Delete draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
